"use client"

import { useState, useEffect, useRef } from 'react';

interface TextFieldProps {
  label?: string;
  value: string; // Controlled component: value comes from parent
  onChange: (value: string) => void; // Function to notify parent of valid changes
  placeholder?: string;
  maxLength?: number;
  externalError?: string; // Error message provided by the parent
  validationRule?: RegExp; // Regex for validation
  validationMessage?: string; // Custom message for validation failure
  prepend?: string; // String to prepend inside the input
  onBlur?: () => void; // Optional onBlur handler
}

const VALIDATION_DEBOUNCE_DELAY = 2000; // 5 seconds

export const TextField: React.FC<TextFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  externalError,
  validationRule,
  validationMessage,
  prepend,
  onBlur
}) => {
  // Internal state to hold the current input value, allowing free typing
  const [inputValue, setInputValue] = useState(value);
  // Internal state for validation errors based on validationRule
  const [internalValidationError, setInternalValidationError] = useState<string | undefined>(undefined);
  // Ref to store the debounce timer ID
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Effect to update internal state if the external value prop changes
  useEffect(() => {
    // Only update if the external value differs from the internal one
    if (value !== inputValue) {
      setInputValue(value);
      // Optionally, clear validation error when external value changes?
      // setInternalValidationError(undefined);
      // if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    }
    // Intentionally excluding inputValue from dependencies to only react to external 'value' prop changes.
  }, [value]);

  // Effect for cleaning up the debounce timer on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []); // Empty dependency array ensures this runs only on mount and unmount

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Enforce maxLength: prevent typing more characters than allowed
    if (maxLength !== undefined && newValue.length > maxLength) {
      return; // Stop the input change
    }

    // Update the internal state immediately to reflect user typing
    setInputValue(newValue);

    // --- Immediate Validation for onChange ---
    // Check if the new value is valid according to the rule
    let isValid = true;
    if (validationRule) {
      isValid = validationRule.test(newValue);
    }

    // Call the external onChange *only* if the input is valid
    if (isValid) {
      onChange(newValue);
    }
    // If invalid, the internal state is updated, but the parent is not notified via onChange yet.

    // --- Debounced Validation for Error Display ---
    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set a new timer to validate and potentially show an error after delay
    debounceTimerRef.current = setTimeout(() => {
      // Re-check validity inside the timer using the value from the closure
      const currentIsValid = validationRule ? validationRule.test(newValue) : true;

      if (!currentIsValid && newValue) { // Show validation error only if invalid and not empty
        setInternalValidationError(validationMessage || "Input does not match the required format");
      } else {
        setInternalValidationError(undefined); // Clear validation error if valid or empty
      }
    }, VALIDATION_DEBOUNCE_DELAY);

    // Also clear the error immediately if the input becomes valid,
    // otherwise the user might see a stale error until the debounce timer fires.
    if (isValid) {
        setInternalValidationError(undefined);
        // If it becomes valid, we might want to clear any pending *error* display timer?
        // This prevents an error message briefly appearing after 5s even if the input is now valid.
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    }
  };

  // Determine the error message to display (prioritize external error)
  const displayError = externalError || internalValidationError;

  return (
    <div className="flex flex-col relative mb-6">
      {label && <label className="text-md font-bold mb-2">{label}</label>}
      <div className="relative">
        {prepend && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-base-content/50">{prepend}</span>}
        <input
          type="text"
          value={inputValue} // Input displays the internal state
          onChange={handleInputChange} // Handle changes internally
          onBlur={onBlur} // Pass through onBlur prop
          placeholder={placeholder}
          // Add error styling if there's a displayable error
          className={`input input-bordered w-full ${prepend ? 'pl-6' : ''} ${displayError ? 'input-error' : ''}`}
        />
      </div>
      {/* Character counter */}
      {maxLength !== undefined && (
        <span className={`absolute -bottom-6 right-0 text-sm ${inputValue.length >= maxLength ? 'text-primary' : 'text-base-content/50'}`}>
          {inputValue.length}/{maxLength}
        </span>
      )}
      {/* Error message display */}
      {displayError && (
        <span className="absolute -bottom-6 left-0 text-sm text-error">
          {displayError}
        </span>
      )}
    </div>
  );
};
